import React, { useEffect, useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import { Button, Modal, Row, Col, Form, OverlayTrigger } from "react-bootstrap";
import { MdFilterList, MdAddCircle, MdEdit, MdDelete } from "react-icons/md";
import { Tooltip } from "@mui/material";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import loading from "../../../assets/images/common/loading.gif";
import DataTable from "react-data-table-component";

const ManageBudget = () => {
    const [routes, setRoutes] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");
    const navigate = useNavigate();
    const [showFilterModal, setShowFilterModal] = useState(false);
    const handleShowFilterModal = () => setShowFilterModal(true);
    const handleCloseFilterModal = () => setShowFilterModal(false);
    const [isLoading, setIsLoading] = useState(true);

    const baseUrl = process.env.REACT_APP_API_BASE_URL;
    const userData = sessionStorage.getItem("user");
    const userObj = userData ? JSON.parse(userData) : {};

    const readOnlyRoles = ["Class Teacher", "Teacher", "Class Incharge", "School Admin"];
    const canSubmit = !readOnlyRoles.includes(userObj.role_name?.trim());
    const [rowDetails, setRowDetails] = useState({});
    const [loadingDetails, setLoadingDetails] = useState({});

    const [form, setForm] = useState({
        route_location_id: "",
        location_name: "",
        school_id: userObj?.school_id || ""
    });

    const [filter, setFilter] = useState({
        school_id: 0,
        from_date: "",
        to_date: "",
        status: "",
        action: "FILTER"
    });

    // Expandable row component
    const ExpandedComponent = ({ data }) => {
        const details = rowDetails[data.budget_entry_id] || [];
        const isLoading = loadingDetails[data.budget_entry_id];

        return (
            <div
                className="expandedTableWrapper"
                style={{
                    marginLeft: "60px",
                    border: "1px solid rgb(204, 204, 204)",
                    borderRadius: "8px",
                    padding: "16px",
                    background: "linear-gradient(135deg, rgb(249, 249, 249) 0%, rgb(230, 230, 230) 100%)",
                    boxShadow: "rgba(0, 0, 0, 0.1) 2px 4px 10px",
                    marginTop: "10px",
                    marginBottom: "10px"
                }}
            >
                <div style={{
                    marginBottom: "12px",
                    fontSize: "16px",
                    fontWeight: "600",
                    color: "#495057",
                    borderBottom: "1px solid #dee2e6",
                    paddingBottom: "8px"
                }}>
                    Account Details for Budget Entry #{data.budget_entry_id}
                </div>

                {isLoading ? (
                    <div className="text-center py-3">
                        <div className="spinner-border spinner-border-sm text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <span className="ms-2">Loading account details...</span>
                    </div>
                ) : details.length > 0 ? (
                    <div style={{ overflow: "auto" }}>
                        <table className="table table-bordered table-hover" style={{
                            fontSize: "14px",
                            margin: 0,
                            minWidth: "100%"
                        }}>
                            <thead style={{ backgroundColor: "#e9ecef" }}>
                                <tr>
                                    <th style={{ padding: "10px", whiteSpace: "nowrap", fontWeight: "600" }}>Account Code</th>
                                    <th style={{ padding: "10px", whiteSpace: "nowrap", fontWeight: "600" }}>Amount</th>
                                </tr>
                            </thead>
                            <tbody>
                                {details.map((item) => (
                                    <tr key={item.budget_entry_detail_id}>
                                        <td style={{ padding: "10px" }}>{item.group_accountname}</td>
                                        <td style={{ padding: "10px" }}>
                                            {parseFloat(item.amount).toLocaleString('en-IN', {
                                                minimumFractionDigits: 2,
                                                maximumFractionDigits: 2
                                            })}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="text-center py-3" style={{ color: "#6c757d" }}>
                        No account details found for this budget entry
                    </div>
                )}
            </div>
        );
    };

    const handleExpandClick = async (budget_entry_id) => {
        if (rowDetails[budget_entry_id]) return; // Already loaded

        setLoadingDetails(prev => ({ ...prev, [budget_entry_id]: true }));
        try {
            const response = await axios.get(
                `${baseUrl}/budgetentry/detail/${budget_entry_id}`
            );
            setRowDetails(prev => ({
                ...prev,
                [budget_entry_id]: response.data
            }));
        } catch (err) {
            console.error("Error fetching budget details:", err);
            toast.error("Failed to load budget details");
        } finally {
            setLoadingDetails(prev => ({ ...prev, [budget_entry_id]: false }));
        }
    };

    const handleInputChange = (e) => {
        const { id, value } = e.target;
        setForm((prevForm) => ({
            ...prevForm,
            [id]: value
        }));
    };

    const fetchRouteLocations = async () => {
        try {
            const response = await axios.post(baseUrl + "/budgetread/", {
                action: "READ",
                // school_id: userObj.school_id
            });
            setRoutes(response.data);
        } catch (error) {
            console.log("Error fetching data:", error);
        }
    };

    const handleFilterSearch = async (e) => {
        e.preventDefault();
        const formData = {
            budget_entry_id: 0,
            budget_from_date: filter.from_date || null,
            budget_to_date: filter.to_date || null,
            school_id: filter.school_id || 0
        };

        try {
            setIsLoading(true);
            const response = await axios.post(baseUrl + "/budgetentryfilter/", formData, {
                headers: {
                    "Content-Type": "application/json",
                },
            });

            // 🔴 CHANGE HERE ONLY: merge filter response with existing routes
            const incoming = Array.isArray(response.data) ? response.data : [];

            const merged = incoming.map((item) => {
                const original =
                    (routes || []).find(
                        (r) => r.budget_entry_id === item.budget_entry_id
                    ) || {};

                // merge original (which has status, etc.) with new filtered data (dates, school)
                const row = { ...original, ...item };

                // if we still don't have any status-like field, set a default
                if (
                    row.status === undefined &&
                    row.budget_status === undefined &&
                    row.status_name === undefined &&
                    row.budgetStatus === undefined
                ) {
                    row.status = "Active"; // default fallback so Status column doesn't go blank
                }

                return row;
            });

            setRoutes(merged);
            setRowDetails({});
            handleCloseFilterModal();
        } catch (error) {
            console.error("Error fetching data:", error);
            toast.error("Failed to filter data");
        } finally {
            setIsLoading(false);
        }
    };

    const handleEditClick = async (budget_entry_id, e) => {
        e?.stopPropagation();

        try {
            // 1. Get main budget entry data
            const selected = routes.find(r => r.budget_entry_id === budget_entry_id);

            if (!selected) {
                toast.error("Budget entry not found");
                return;
            }

            // 2. Get budget details (this matches the .NET API call)
            let budgetDetails = [];
            try {
                const detailResponse = await axios.get(
                    `${baseUrl}/budgetentry/detail/${budget_entry_id}`
                );
                budgetDetails = detailResponse.data || [];
            } catch (error) {
                console.error("Error fetching budget details for edit:", error);
                toast.error("Error loading budget details");
                return;
            }

            // 3. Format the data EXACTLY as your .NET backend expects
            const editData = {
                // Main form fields (from row data)
                budget_entry_id: selected.budget_entry_id,
                project_id: selected.school_id || userObj.school_id, // Changed to match .NET's project_id
                project_name: selected.school_name || userObj.school_name,
                budget_from_date: selected.budget_from_date,
                budget_to_date: selected.budget_to_date,

                // Grid data formatted as your .NET code shows
                budgetList: budgetDetails.map((detail, index) => ({
                    id: index + 1,
                    budget_entry_detail_id: detail.budget_entry_detail_id,
                    account_master_id: detail.account_master_id,
                    group_accountname: detail.group_accountname,
                    amount: detail.amount,
                    from_date: selected.budget_from_date,
                    to_date: selected.budget_to_date
                })),

                // Additional fields from your .NET form
                hdnABudgetEntryId: selected.budget_entry_id,
                hdnBEDId: "", // Empty for new entries

                // Flags
                isEditMode: true,
                datesLocked: true, // Always locked in edit mode
                action: "UPDATE"
            };

            // 4. Navigate with data
            navigate("/finance/budget/add", {
                state: {
                    routeData: editData
                }
            });

        } catch (error) {
            console.error("Error preparing edit data:", error);
            toast.error("Failed to load budget data for editing");
        }
    };

    const handleDeleteClick = async (budget_entry_id) => {
        if (!window.confirm("Are you sure you want to change the status?")) return;

        try {
            await axios.post(`${baseUrl}/delete-budget-entry/`, {
                action: "DELETE",
                budget_entry_id,
                modified_by: "admin"
            });
            toast.success("Record Set To InActive");
            fetchRouteLocations();
        } catch (error) {
            console.error("Error deleting route location:", error);
        }
    };

    // 👇 unchanged: helper to read status
    const getRowStatus = (row) => {
        return (
            row?.status ??
            row?.budget_status ??
            row?.budgetStatus ??
            row?.status_name ??
            ""
        );
    };

    const searchableColumns = [
        (row) => row.school_name,
        (row) =>
            row.budget_from_date
                ? new Date(row.budget_from_date).toLocaleDateString("en-GB")
                : "",
        (row) =>
            row.budget_to_date
                ? new Date(row.budget_to_date).toLocaleDateString("en-GB")
                : "",
        (row) => getRowStatus(row),
    ];

    const filteredRecords = (routes || []).filter((item) =>
        searchableColumns.some((selector) => {
            const value = selector(item);
            const stringValue = String(value || '').toUpperCase().replace(/[-\s]+/g, '');
            const normalizedQuery = searchQuery.toUpperCase().replace(/[-\s]+/g, '');
            return stringValue.includes(normalizedQuery);
        })
    );

    const columns = [
        {
            name: "School Name",
            selector: (row) => row.school_name,
            sortable: true,
            cell: (row) => (
                <Tooltip title={row.school_name}>
                    <span className="cell-wrap">{row.school_name}</span>
                </Tooltip>
            ),
            minWidth: "150px",
        },
        {
            name: "From Date",
            selector: (row) => row.budget_from_date,
            sortable: true,
            cell: (row) => {
                const formattedDate = row.budget_from_date
                    ? new Date(row.budget_from_date).toLocaleDateString("en-GB")
                    : "";
                return (
                    <Tooltip title={formattedDate}>
                        <span className="cell-wrap">{formattedDate}</span>
                    </Tooltip>
                );
            },
            minWidth: "120px",
        },
        {
            name: "To Date",
            selector: (row) => row.budget_to_date,
            sortable: true,
            cell: (row) => {
                // ✅ Handle No Records row
                if (row.budget_entry_id === "No Records Found") {
                    return (
                        <span style={{ color: "red", fontWeight: 600 }}>
                            No Records Found
                        </span>
                    );
                }

                const formattedDate = row.budget_to_date
                    ? new Date(row.budget_to_date).toLocaleDateString("en-GB")
                    : "";

                return (
                    <Tooltip title={formattedDate}>
                        <span className="cell-wrap">{formattedDate}</span>
                    </Tooltip>
                );
            },
            minWidth: "120px",
        },
        {
            name: "Status",
            selector: (row) => getRowStatus(row),
            sortable: true,
            cell: (row) => {
                const status = getRowStatus(row);
                return (
                    <Tooltip title={status}>
                        <span className="cell-wrap">{status}</span>
                    </Tooltip>
                );
            },
            minWidth: "90px",
        },
        ...(canSubmit
            ? [
                {
                    name: "Actions",
                    cell: (row) => {
                        if (
                            !row.budget_entry_id ||
                            row.budget_entry_id === "No Records Found"
                        ) {
                            return null;
                        }

                        return (
                            <div className="tableActions">
                                <Tooltip title="Edit" arrow>
                                    <span
                                        className="commonActionIcons"
                                        onClick={(e) =>
                                            handleEditClick(row.budget_entry_id, e)
                                        }
                                    >
                                        <MdEdit />
                                    </span>
                                </Tooltip>
                                <Tooltip title="Delete" arrow>
                                    <span
                                        className="commonActionIcons"
                                        onClick={() => handleDeleteClick(row.budget_entry_id)}
                                    >
                                        <MdDelete />
                                    </span>
                                </Tooltip>
                            </div>
                        );
                    },
                    minWidth: "120px",
                    center: true,
                },
            ]
            : [])
    ];

    // 🔧 Reset (unchanged)
    const handleReset = async (e) => {
        e.preventDefault();
        setFilter({
            school_id: 0,
            from_date: "",
            to_date: "",
            status: "",
            action: "FILTER"
        });
        setRowDetails({});
        await fetchRouteLocations();
    };

    const handleSearchChange = (event) => {
        setSearchQuery(event.target.value);
    };

    useEffect(() => {
        setIsLoading(true);
        fetchRouteLocations().finally(() => setIsLoading(false));
    }, []);

    return (
        <div className="pageBody">
            <ToastContainer />
            <div className="commonDataTableHead">
                <div className="d-flex justify-content-between align-items-center w-100">
                    <div className="d-flex align-items-center" style={{ gap: "10px" }}>
                        <h6 className="commonTableTitle">Manage Budget</h6>
                    </div>
                    <div>
                        <input
                            type="text"
                            placeholder="Search..."
                            value={searchQuery}
                            className="searchInput"
                            onChange={handleSearchChange}
                        />
                    </div>
                    <div className="d-flex align-items-center" style={{ gap: 6 }}>
                        <OverlayTrigger placement="top" overlay={<Tooltip id="tooltip-top">Filter</Tooltip>}>
                            <Button className="secondaryBtn" variant="secondary" onClick={handleShowFilterModal}>
                                <MdFilterList />
                            </Button>
                        </OverlayTrigger>
                        <OverlayTrigger placement="top" overlay={<Tooltip id="tooltip-top">Add</Tooltip>}>
                            <Button className="primaryBtn" variant="primary" onClick={() => navigate("/finance/budget/add")}>
                                <MdAddCircle />
                            </Button>
                        </OverlayTrigger>
                    </div>
                </div>
            </div>

            <div className="commonTable height100">
                <div className="tableBody">
                    {isLoading ? (
                        <div className="loadingContainer">
                            <img src={loading} alt="Loading..." className="loadingGif" />
                        </div>
                    ) : (
                        <DataTable
                            className="custom-table"
                            columns={columns}
                            data={
                                Array.isArray(filteredRecords) && filteredRecords.length > 0
                                    ? filteredRecords
                                    : [
                                        {
                                            budget_entry_id: "No Records Found",
                                            school_name: "",
                                            budget_from_date: "",
                                            budget_to_date: "No Records Found",
                                        }
                                    ]
                            }
                            expandableRows={
                                Array.isArray(filteredRecords) && filteredRecords.length > 0
                            }
                            expandableRowsComponent={ExpandedComponent}
                            expandableRowExpanded={(row) => row.budget_entry_id && rowDetails[row.budget_entry_id]}
                            onRowExpandToggled={(expanded, row) => {
                                if (expanded && row.budget_entry_id) {
                                    handleExpandClick(row.budget_entry_id);
                                }
                            }}
                            expandableRowsHideExpander={false}
                            pagination={
                                Array.isArray(filteredRecords) && filteredRecords.length > 0
                            }
                            highlightOnHover
                            paginationPerPage={10}
                            responsive
                            fixedHeader
                            fixedHeaderScrollHeight="calc(100vh - 250px)"
                            conditionalRowStyles={[
                                {
                                    when: (row) => row.budget_entry_id === "No Records Found",
                                    style: {
                                        textAlign: "center",
                                        fontSize: "16px",
                                        color: "red",
                                        backgroundColor: "#f9f9f9",
                                    },
                                },
                            ]}
                            customStyles={{
                                headRow: {
                                    style: {
                                        backgroundColor: "#f8f9fa",
                                        borderBottom: "2px solid #dee2e6",
                                        fontWeight: "600",
                                    },
                                },
                                rows: {
                                    style: {
                                        cursor: "pointer",
                                        "&:hover": {
                                            backgroundColor: "#f5f5f5",
                                        },
                                    },
                                },
                                expanderButton: {
                                    style: {
                                        cursor: "pointer",
                                        fontSize: "20px",
                                        fontWeight: "bold",
                                    },
                                },
                            }}
                        />
                    )}
                </div>
            </div>

            {/* Filter Modal */}
            <Modal show={showFilterModal} onHide={handleCloseFilterModal} className="commonFilterModal">
                <Modal.Header closeButton className="modalHeaderFixed">
                    <Modal.Title>Filter</Modal.Title>
                </Modal.Header>
                <Modal.Body className="modalBodyScrollable">
                    <Row>
                        {/* School filter commented out as in your original */}
                        <Col xs={12}>
                            <div className="commonInput">
                                <Form.Group controlId="from_date">
                                    <Form.Label>From Date</Form.Label>
                                    <Form.Control
                                        type="date"
                                        value={filter.from_date}
                                        onChange={(e) =>
                                            setFilter({ ...filter, from_date: e.target.value })
                                        }
                                    />
                                </Form.Group>
                            </div>
                        </Col>

                        <Col xs={12}>
                            <div className="commonInput">
                                <Form.Group controlId="to_date">
                                    <Form.Label>To Date</Form.Label>
                                    <Form.Control
                                        type="date"
                                        value={filter.to_date}
                                        min={filter.from_date || undefined}  // ✅ prevents invalid range
                                        onChange={(e) =>
                                            setFilter({ ...filter, to_date: e.target.value })
                                        }
                                    />
                                </Form.Group>
                            </div>
                        </Col>
                    </Row>
                </Modal.Body>
                <Modal.Footer className="modalFooterFixed">
                    <Button variant="secondary" className="btn-info clearBtn me-2" onClick={handleReset}>
                        Reset
                    </Button>
                    <div>
                        <Button variant="secondary" className="btn-danger secondaryBtn me-2" onClick={handleCloseFilterModal}>
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            className="btn-success primaryBtn"
                            onClick={handleFilterSearch}
                        >
                            Search
                        </Button>
                    </div>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default ManageBudget;
